import { Divider, List, ListItem, ListItemText, Typography, useMediaQuery, useTheme } from "@mui/material";
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Grid from '@mui/material/Grid2';
import Link from '@mui/material/Link';
import { LineChart } from '@mui/x-charts/LineChart';
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AdsComponent from "../../components/AdComponent";
import CustomButton from '../../components/CustomButton';
import CustomCard from '../../components/CustomCard';
import CustomTextField from '../../components/CustomTextField';
import CustomTypography from "../../components/CustomTypography";
import { triggerGTMEvent } from "../../components/GTMEvent";
import { CurrencyFormatterUtils } from '../currency-formatter/currency-formatter-utils';
import * as Data from './lumpsuminvestment-calculator-data';
import { setExpectedReturnRate, setLumpsumAmount, setTenureInYears } from './lumpsuminvestment-calculator-reducer'; // Path to your reducer
import * as Utils from './lumpsuminvestment-calculator-utils';

const Example = ({ router }) => {
    const dispatch = useDispatch();
    const lumpsumAmount = useSelector((state) => state.lumpsumInvestmentCalculator.lumpsumAmount);
    const tenureInYears = useSelector((state) => state.lumpsumInvestmentCalculator.tenureInYears);
    const expectedReturnRate = useSelector((state) => state.lumpsumInvestmentCalculator.expectedReturnRate);
    const currencyFormatter = useSelector((state) => state.currencyDataFormatter.formatter || {});
    const [report, setReport] = useState(Utils.generateReport(lumpsumAmount, tenureInYears, expectedReturnRate));

    const scrollToTopRef = useRef(null);
    useEffect(() => {
        scrollToTopRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    const navigateTo = (path) => {
        router.navigate(path); // This navigates to the specific page
    };

    const handleLumpsumAmount = (event) => {
        dispatch(setLumpsumAmount(event.target.value));
        setReport(null);
    }

    const handleTenureInYears = (event) => {
        dispatch(setTenureInYears(event.target.value));
        setReport(null);
    }

    const handleExpectedReturnRate = (event) => {
        dispatch(setExpectedReturnRate(event.target.value));
        setReport(null);
    }

    const generateReport = () => {
        triggerGTMEvent("Generate Report", "LUMPSUM > Generate Report");
        const report = Utils.generateReport(lumpsumAmount, tenureInYears, expectedReturnRate);
        setReport(report);
    };

    return (
        <div ref={scrollToTopRef}>

            {/* BREADCRUMPS WIDGET START */}
            <Grid container spacing={1} >
                <Grid item size={{ xs: 12, sm: 12, md: 12 }} sx={{ margin: '10px' }}>
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link underline="hover" color="inherit" onClick={() => navigateTo('/')}>
                            Home
                        </Link>
                        <Typography sx={{ color: 'text.primary' }}>Lumpsum Investment Calculator</Typography>
                    </Breadcrumbs>
                </Grid>
            </Grid>
            {/* BREADCRUMPS WIDGET ENDS */}

            {/* INPUT WIDGET START */}
            <Grid container spacing={1}>
                <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
                    <CustomCard
                        title="Lumpsum Investment"
                    >
                        <CustomTextField
                            label="Lumpsum Amount"
                            value={lumpsumAmount}
                            type="number"
                            onChange={handleLumpsumAmount}
                        />
                        <CustomTextField
                            label="Tenure (In Years)"
                            value={tenureInYears}
                            type="number"
                            onChange={handleTenureInYears}
                        />
                        <CustomTextField
                            label="Expected Return Rate"
                            value={expectedReturnRate}
                            type="number"
                            onChange={handleExpectedReturnRate}
                        />
                        <CustomButton
                            variant="contained"
                            color="success"
                            onClick={generateReport}
                            fullWidth={true}
                        >
                            Calculate
                        </CustomButton>
                    </CustomCard>
                </Grid>
                <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
                    {report && <div>
                        <CustomCard
                            title="Profit"
                            description={
                                <>Your investment of <strong>{CurrencyFormatterUtils(Data.getInvestedAmount(report), currencyFormatter)}</strong> will earn you a profit of</>
                            }
                        >
                            <CustomTypography variant="h6" color="success" fontWeight="bold">
                                {CurrencyFormatterUtils(Data.getProfitAmount(report), currencyFormatter)}  ({Data.getProfitPercentage(report)}%)
                            </CustomTypography>
                        </CustomCard>
                        <CustomCard
                            title="Corpus"
                            description="You'll receive a corpus at the end of the tenure, amounting to"
                        >
                            <CustomTypography variant="h6" color="success" fontWeight="bold">
                                {CurrencyFormatterUtils(Data.getCorpus(report), currencyFormatter)}
                            </CustomTypography>
                        </CustomCard>
                    </div>}
                </Grid>
            </Grid>
            {/* INPUT WIDGET END */}


            {/* {
                report && !isMobile &&
                <Grid container spacing={1} >
                    <Grid size={12}>
                        <CustomCard title="Investment vs Profit vs Corpus">
                            <LineChart
                                dataset={report.reportOfMultipleYears}
                                xAxis={Data.graphXAxis}
                                series={Data.graphSeries}
                                width={970}
                                height={400}
                                margin={{ left: 100, right: 100 }}
                            />
                        </CustomCard>
                    </Grid>
                </Grid>
            } */}
            {/* REPORT WIDGET END */}

            {/* AD WIDGET START */}
            {/* <Grid container spacing={1} >
                <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                    <AdsComponent dataAdSlot='4961665175' />
                </Grid>
            </Grid> */}
            {/* AD WIDGET END */}

            {/* BLOG WIDGET START */}
            <CustomCard>
                <CustomTypography variant="h5" gutterBottom>
                    Why Use Our Lump Sum Investment Calculator? Understanding the Benefits
                </CustomTypography>
                <Divider sx={{ mb: 2 }} />

                <CustomTypography variant="body1" paragraph>
                    Making smart investment decisions is crucial for growing your wealth. Our Lump Sum Investment Calculator helps you
                    project the future value of your one-time investment, giving you clarity on potential returns. Here’s why this tool
                    is beneficial:
                </CustomTypography>

                <List>
                    <ListItem>
                        <ListItemText
                            primary="1. Accurate Return Estimation"
                            secondary="With just a few inputs—investment amount, expected return rate, and investment period—you can instantly calculate your future returns. This helps set realistic financial goals."
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemText
                            primary="2. Understand Compound Growth"
                            secondary="The calculator demonstrates how compound interest works over time, showing you how reinvested earnings can significantly boost your wealth."
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemText
                            primary="3. Visualize Wealth Accumulation"
                            secondary="Get a clear picture of how your investment grows over the years. This transparency helps you align your investments with long-term financial plans."
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemText
                            primary="4. Compare Investment Scenarios"
                            secondary="Adjust the return rate or investment period to compare different growth scenarios and choose the best strategy to maximize your returns."
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemText
                            primary="5. Better Financial Planning"
                            secondary="Knowing your potential returns helps you plan for future expenses, whether it's buying a home, funding education, or saving for retirement."
                        />
                    </ListItem>
                </List>

                <CustomTypography variant="h5" gutterBottom>
                    How to Use the Lump Sum Investment Calculator Effectively?
                </CustomTypography>
                <Divider sx={{ mb: 2 }} />

                <List>
                    <ListItem>
                        <ListItemText primary="Enter Investment Details" secondary="Provide the lump sum amount, expected return rate, and investment period." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Analyze the Growth Report" secondary="Understand how much your investment will grow over the chosen period." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Experiment with Different Values" secondary="Try different return rates and durations to see how they affect your investment." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Plan Your Financial Goals" secondary="Use the projected returns to plan for milestones like retirement, education, or large purchases." />
                    </ListItem>
                </List>

                <CustomTypography variant="h5" gutterBottom>
                    Final Thoughts
                </CustomTypography>
                <Divider sx={{ mb: 2 }} />
                <CustomTypography variant="body1" paragraph>
                    Our Lump Sum Investment Calculator simplifies the process of projecting future returns and helps you make informed
                    decisions. Whether you're investing for short-term gains or long-term growth, this tool offers valuable insights
                    to guide your investment journey.
                </CustomTypography>
                <CustomTypography variant="body1" paragraph>
                    Try it now and take the first step towards achieving your financial goals!
                </CustomTypography>
            </CustomCard>
            {/* BLOG WIDGET END */}
        </div >
    );
};

export default Example;
